/**
 * Obstruction Detail Content
 *
 * Detail content for the Obstruction artifact, rendered inside
 * `UnifiedDetailOverlay`. Mirrors `TaskDetailContent` / `NoteDetailContent`:
 *
 * - Publishes header content + toolbar callbacks to the overlay shell via
 *   `useRegisterDetailSlots`.
 * - Reads fullscreen mode from `useDetailOverlayFullscreen` and switches
 *   between `tabs` and `sidebar` layouts in `DetailPanelLayout`.
 * - Tracks the active section locally via `useObstructionDetailsState`
 *   and consumes section availability from `useObstructionDetailsSectionTabs`.
 *
 * Geometry / signs tabs land with Phase 2 of the obstruction roadmap —
 * Wave 3 only ships the Overview / Contractor / History sections.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react';

import { DetailPanelLayout } from '@/components/common/DetailPanelLayout';
import { DeleteConfirmationDialog } from '@/components/ui/DeleteConfirmationDialog';
import { LoadingState } from '@/components/common/loading';
import {
  useDetailOverlayFullscreen,
  useRegisterDetailSlots,
} from '@/components/overlays/UnifiedDetailOverlay';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import { useArtifactNavigation } from '@/hooks/useArtifactNavigation';
import { useGeoMarkupStore } from '@/store';
import { selectDetailedObstruction } from '@/store/selectors/obstructionSelectors';

import {
  ObstructionDetailsContent,
  ObstructionDetailsHeader,
} from './components';
import { useObstructionDetailsSectionTabs } from './hooks/useObstructionDetailsSectionTabs';
import { useObstructionDetailsState } from './hooks/useObstructionDetailsState';
import styles from './ObstructionDetailsPanel.module.css';

function hasContractorContent(
  contractor: { company: string; manager: string; phone: string } | undefined
): boolean {
  if (!contractor) return false;
  return (
    contractor.company.length > 0 ||
    contractor.manager.length > 0 ||
    contractor.phone.length > 0
  );
}

export function ObstructionDetailContent(): ReactElement | null {
  const selectedObstruction = useGeoMarkupStore(selectDetailedObstruction);
  const isSyncing = useGeoMarkupStore((state) => state.obstructions.isSyncing);
  const fetchObstructionByShortId = useGeoMarkupStore(
    (state) => state.obstructions.fetchObstructionByShortId
  );
  const deleteObstruction = useGeoMarkupStore(
    (state) => state.obstructions.deleteObstruction
  );
  const previewMode = useGeoMarkupStore(
    (state) => state.ui.slidePanel.previewMode
  );
  const setPreviewMode = useGeoMarkupStore((state) => state.ui.setPreviewMode);

  const { isMobile } = useDeviceLayout();
  const { isFullscreenMode } = useDetailOverlayFullscreen();
  const { openArtifact, closeOverlay } = useArtifactNavigation();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Refresh full detail when the selected obstruction changes (mirrors TaskDetailContent).
  useEffect(() => {
    if (selectedObstruction?.id) {
      fetchObstructionByShortId(selectedObstruction.id);
    }
  }, [selectedObstruction?.id, fetchObstructionByShortId]);

  const hasContractor = hasContractorContent(selectedObstruction?.contractor);
  const hasAttachments = (selectedObstruction?.attachments?.length ?? 0) > 0;

  const { sectionTabs, availableTabs } = useObstructionDetailsSectionTabs({
    hasContractor,
    hasAttachments,
  });

  const { activeTab, setActiveTab } = useObstructionDetailsState(
    selectedObstruction?.id,
    { availableTabs }
  );

  const handleSectionTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId as typeof activeTab);
    },
    [setActiveTab]
  );

  const handleEdit = useCallback(() => {
    if (selectedObstruction) {
      openArtifact('obstructions', 'edit', selectedObstruction.shortId);
    }
  }, [selectedObstruction, openArtifact]);

  const handleDeleteClick = useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteDialog(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedObstruction) return;
    const success = await deleteObstruction(selectedObstruction.id);
    if (success) {
      setShowDeleteDialog(false);
      closeOverlay();
    }
  }, [selectedObstruction, deleteObstruction, closeOverlay]);

  const showSidebar = isFullscreenMode && !isMobile;

  // Hide the inline header when we render the sidebar header instead.
  const headerContent = useMemo(() => {
    if (!selectedObstruction || showSidebar) return undefined;
    return (
      <div className={styles.headerStack}>
        <ObstructionDetailsHeader obstruction={selectedObstruction} />
      </div>
    );
  }, [selectedObstruction, showSidebar]);

  useRegisterDetailSlots({
    headerContent,
    onEdit: selectedObstruction ? handleEdit : undefined,
    onDelete: selectedObstruction ? handleDeleteClick : undefined,
    fullscreenWideContent: showSidebar,
    defaultFullscreen: false,
    previewMode: previewMode && isMobile,
    onPreviewModeChange: setPreviewMode,
  });

  if (!selectedObstruction && !isSyncing) {
    return null;
  }

  if (!selectedObstruction) {
    return (
      <div className={styles.loadingContainer}>
        <LoadingState centered />
      </div>
    );
  }

  return (
    <>
      <DetailPanelLayout
        mode={showSidebar ? 'sidebar' : 'tabs'}
        sectionItems={sectionTabs}
        activeSection={activeTab}
        onSectionChange={handleSectionTabChange}
        sidebarHeader={
          <ObstructionDetailsHeader obstruction={selectedObstruction} />
        }
        contentVariant={activeTab === 'history' ? 'flex' : 'default'}
        widePreview
      >
        <ObstructionDetailsContent
          activeTab={activeTab}
          obstruction={selectedObstruction}
        />
      </DetailPanelLayout>

      <DeleteConfirmationDialog
        isOpen={showDeleteDialog}
        itemName={selectedObstruction.title}
        itemType="obstruction"
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
